import {
  defaultEntityState,
  IEntityState,
  INormalizedEntity,
} from '@core/state/entities/entity';
import {
  mergeEntityState,
  removeEntities,
  removeEntitiesByFilter,
  removeEntitiesWithIds,
} from '@core/state/entities/entity.helpers';
import {
  IProject,
  ProjectChildKeys,
} from '@core/state/entities/project/project.entity';
import { IProjectChild } from '../entity';
import _ from 'lodash-es';
import { entityKey } from '../entity-name-map';
import { createReducer, on } from '@ngrx/store';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { IProjectDetails } from '@core/state/entities/project-details/project-details.entity';
import { IProjectSecureDetails } from '@core/state/entities/project-secure-details/project-secure-details.entity';

export const childIdsToProjectFromNormalizedPayload = <
  TKey extends ProjectChildKeys,
  TProjectParent extends IProject | IProjectDetails | IProjectSecureDetails,
>(
  key: TKey,
  state: IEntityState<TProjectParent>,
  props: INormalizedEntity<TKey>,
  remove?: boolean
): IEntityState<TProjectParent> => {
  const { entities } = props;
  const objectMap = entities[key];
  const objectArray: IProjectChild[] = _.values(objectMap) as IProjectChild[];
  return childIdsToProject(key, state, objectArray, remove);
};

export const childIdsToProject = <
  TKey extends ProjectChildKeys,
  TProjectParent extends IProject | IProjectDetails | IProjectSecureDetails,
>(
  key: TKey,
  state: IEntityState<TProjectParent>,
  objectArray: IProjectChild[],
  remove?: boolean
): IEntityState<TProjectParent> => {
  // check for multiple results (just get first if array)
  if (objectArray.length < 1) {
    return state;
  }

  const project = state.byId[objectArray[0].projectId];

  const operation = remove ? _.difference : _.union;

  return {
    ...state,
    ...{
      byId: {
        ...state.byId,
        [project.id]: {
          ...project,
          ...{
            [key]: remove
              ? _.difference(
                  project[key as string],
                  objectArray.map((o) => o.id)
                )
              : _.union(
                  objectArray.map((o) => o.id),
                  project[key as string]
                ),
          },
        },
      },
    },
  };
};
export const projectEntityReducer = createReducer(
  defaultEntityState<IProject>(),
  on(projectDetailActions.projectListingsLoaded, (state, props) =>
    mergeEntityState(entityKey.projects, state, props, true)
  ),
  on(
    projectDetailActions.created,
    projectDetailActions.detailLoaded,
    projectDetailActions.projectForInvitationRetrieved,
    projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
    projectDetailActions.requestToJoinProjectResponseReceived,
    projectDetailActions.invitationsBulkRevokedSuccessfully,
    projectDetailActions.requestToJoinProjectCancelled,
    projectDetailActions.joinRequestsResponseSent,
    projectDetailActions.invitationResponseReceived,
    projectDetailActions.registered,
    projectDetailActions.projectListingUpdated,
    projectDetailActions.projectUpdated,
    (state, props) => mergeEntityState(entityKey.projects, state, props)
  ),

  // Revision-2 README §6.5: a guidance write returns only the project's guidance map, so patch
  // that one field rather than replacing an entity the response never described.
  on(
    projectDetailActions.projectCategoryGuidanceUpdated,
    (state, props): IEntityState<IProject> => {
      const project = state.byId[props.projectId];
      if (!project) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [props.projectId]: {
            ...project,
            categoryGuidance: props.categoryGuidance,
          },
        },
      };
    }
  ),

  // FEAT-025: the keyword write returns only the normalised lists, so patch those two fields.
  on(
    projectDetailActions.screeningKeywordsUpdated,
    (state, props): IEntityState<IProject> => {
      const project = state.byId[props.projectId];
      if (!project) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [props.projectId]: {
            ...project,
            screeningInclusionKeywords: props.inclusionKeywords,
            screeningExclusionKeywords: props.exclusionKeywords,
          },
        },
      };
    }
  ),

  on(projectDetailActions.projectDeleted, (state, props) =>
    removeEntitiesByFilter(state, (project) => project.id === props.projectId)
  )
);
